/* ------------------------------------------------------------------
  File: Carousel.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-07-05
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable carousel component for the Accend Holdings website.
//   - Supports direct React children such as card components.
//   - Also supports data-driven items using renderItem.
//   - Supports responsive slidesPerView using breakpoint config.
//   - Supports previous/next controls, dot pagination, keyboard navigation,
//     autoplay, pause on hover, infinite looping, and custom positioning.
//   - Uses the acc-carousel namespace to avoid conflicts with Bootstrap.

import {
  Children,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import "./Carousel.css";
import Button from "../Buttons";

const normalizeCarouselGap = (gap) => {
  if (typeof gap === "number") {
    return `${gap}px`;
  }

  if (typeof gap === "string") {
    return gap;
  }

  return "var(--space-24)";
};

const getResponsiveSlidesPerView = ({
  responsive,
  defaultSlidesPerView,
  viewportWidth,
}) => {
  if (!responsive || typeof responsive !== "object") {
    return defaultSlidesPerView;
  }

  const breakpoints = Object.keys(responsive)
    .map(Number)
    .filter((breakpoint) => !Number.isNaN(breakpoint))
    .sort((a, b) => b - a);

  const matchedBreakpoint = breakpoints.find(
    (breakpoint) => viewportWidth >= breakpoint
  );

  if (matchedBreakpoint === undefined) {
    return defaultSlidesPerView;
  }

  return responsive[matchedBreakpoint];
};

const Carousel = ({
  children,
  items = [],
  renderItem,

  slidesPerView = 1,
  responsive,

  gap = 24,
  showControls = true,
  showDots = true,
  infinite = true,

  autoPlay = false,
  autoPlayDelay = 5000,
  pauseOnHover = true,

  variant = "default",
  label = "Carousel",
  className = "",

  controlPosition = "bottom-left",
  dotsPosition = "bottom-center",

  ...props
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === "undefined") {
      return 1440;
    }

    return window.innerWidth;
  });

  const carouselRef = useRef(null);

  const childSlides = useMemo(() => {
    return Children.toArray(children);
  }, [children]);

  const hasChildren = childSlides.length > 0;

  const slides = useMemo(() => {
    if (hasChildren) {
      return childSlides;
    }

    return items;
  }, [hasChildren, childSlides, items]);

  const totalSlides = slides.length;

  const responsiveSlidesPerView = getResponsiveSlidesPerView({
    responsive,
    defaultSlidesPerView: slidesPerView,
    viewportWidth,
  });

  const safeSlidesPerView = Math.max(
    1,
    Math.min(Number(responsiveSlidesPerView) || 1, totalSlides || 1)
  );

  // const safeGap = Number(gap) || 0;

  const safeGap = normalizeCarouselGap(gap);

  const maxIndex = Math.max(totalSlides - safeSlidesPerView, 0);

  const canSlide = totalSlides > safeSlidesPerView;

  const carouselClasses = [
    "acc-carousel",
    `acc-carousel--${variant}`,
    `acc-carousel--controls-${controlPosition}`,
    `acc-carousel--dots-${dotsPosition}`,
    showControls ? "acc-carousel--with-controls" : "",
    showDots ? "acc-carousel--with-dots" : "",
    canSlide ? "acc-carousel--is-scrollable" : "acc-carousel--is-static",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // const slideStyle = useMemo(
  //   () => ({
  //     flex: `0 0 calc((100% - (${safeGap}px * (${safeSlidesPerView} - 1))) / ${safeSlidesPerView})`,
  //   }),
  //   [safeGap, safeSlidesPerView]
  // );

  const slideStyle = useMemo(
    () => ({
      flex: `0 0 calc((100% - (${safeGap} * (${safeSlidesPerView} - 1))) / ${safeSlidesPerView})`,
    }),
    [safeGap, safeSlidesPerView]
  );

  // const trackStyle = useMemo(
  //   () => ({
  //     gap: `${safeGap}px`,
  //     transform: `translateX(calc(-${activeIndex} * ((100% - (${safeGap}px * (${safeSlidesPerView} - 1))) / ${safeSlidesPerView} + ${safeGap}px)))`,
  //   }),
  //   [activeIndex, safeGap, safeSlidesPerView]
  // );

  const trackStyle = useMemo(
    () => ({
      gap: safeGap,
      transform: `translateX(calc(-${activeIndex} * ((100% - (${safeGap} * (${safeSlidesPerView} - 1))) / ${safeSlidesPerView} + ${safeGap})))`,
    }),
    [activeIndex, safeGap, safeSlidesPerView]
  );

  const goToSlide = useCallback(
    (index) => {
      if (!totalSlides || !canSlide) return;

      if (infinite) {
        if (index < 0) {
          setActiveIndex(maxIndex);
          return;
        }

        if (index > maxIndex) {
          setActiveIndex(0);
          return;
        }
      }

      setActiveIndex(Math.min(Math.max(index, 0), maxIndex));
    },
    [totalSlides, canSlide, infinite, maxIndex]
  );

  const goToPrevious = useCallback(() => {
    goToSlide(activeIndex - 1);
  }, [activeIndex, goToSlide]);

  const goToNext = useCallback(() => {
    goToSlide(activeIndex + 1);
  }, [activeIndex, goToSlide]);

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goToPrevious();
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      goToNext();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (!autoPlay || !canSlide || isPaused) {
      return;
    }

    const interval = window.setInterval(() => {
      goToSlide(activeIndex + 1);
    }, autoPlayDelay);

    return () => {
      window.clearInterval(interval);
    };
  }, [activeIndex, autoPlay, autoPlayDelay, canSlide, isPaused, goToSlide]);

  useEffect(() => {
    if (activeIndex > maxIndex) {
      setActiveIndex(maxIndex);
    }
  }, [activeIndex, maxIndex]);

  if (!totalSlides) {
    return null;
  }

  const canGoPrevious = infinite || activeIndex > 0;
  const canGoNext = infinite || activeIndex < maxIndex;
  const dotCount = maxIndex + 1;

  return (
    <section
      ref={carouselRef}
      className={carouselClasses}
      aria-roledescription="carousel"
      aria-label={label}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => pauseOnHover && setIsPaused(true)}
      onMouseLeave={() => pauseOnHover && setIsPaused(false)}
      {...props}
    >
      <div className="acc-carousel__viewport">
        <div className="acc-carousel__track" style={trackStyle}>
          {slides.map((slide, index) => (
            <div
              key={slide?.id || slide?.key || index}
              className="acc-carousel__slide"
              style={slideStyle}
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${totalSlides}`}
            >
              {hasChildren
                ? slide
                : typeof renderItem === "function"
                  ? renderItem(slide, index)
                  : slide}
            </div>
          ))}
        </div>
      </div>

      {(showControls || showDots) && canSlide && (
        <div className="acc-carousel__navigation">
          {showControls && (
            <div className="acc-carousel__controls l-cluster l-cluster--spaced">
              <Button
                aria-label="Previous slide"
                disabled={!canGoPrevious}
                onClick={goToPrevious}
                className="acc-carousel__control acc-carousel__control--previous"
              >
                <LuChevronLeft aria-hidden="true" />
              </Button>
              
              <Button
                aria-label="Next slide"
                disabled={!canGoNext}
                onClick={goToNext}
                className="acc-carousel__control acc-carousel__control--next"
              >
                <LuChevronRight aria-hidden="true" />
              </Button>
            </div>
          )}

          {showDots && dotCount > 1 && (
            <div
              className="acc-carousel__dots l-cluster l-cluster--tight"
              role="tablist"
              aria-label="Carousel slides"
            >
              {Array.from({ length: dotCount }, (_, index) => {
                const isActive = index === activeIndex;

                return (
                  <button
                    key={index}
                    type="button"
                    role="tab"
                    className={[
                      "acc-carousel__dot",
                      isActive ? "acc-carousel__dot--active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-label={`Go to slide ${index + 1}`}
                    aria-selected={isActive}
                    onClick={() => goToSlide(index)}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default Carousel;